/**
 * Push notification device-token registration.
 *
 * Uses @capacitor-firebase/messaging (swapped from @capacitor/push-notifications
 * in this phase) for token acquisition. FirebaseMessaging.getToken() resolves
 * an FCM token directly on both platforms — including iOS, which previously
 * yielded a raw APNs token this backend couldn't address.
 */
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, type PermissionStatus, type TokenReceivedEvent } from '@capacitor-firebase/messaging';
import { Preferences } from '@capacitor/preferences';
import type { apiFetch as ApiFetch } from '@/lib/apiClient';

const INSTALL_ID_KEY = 'push_install_id';
const DEVICE_TOKEN_KEY = 'push_device_token';

let listenersAttached = false;

/** Dynamic import to avoid a circular module graph with apiClient.ts -> clearSession.ts -> here. */
async function getApiFetch(): Promise<typeof ApiFetch> {
  const mod = await import('@/lib/apiClient');
  return mod.apiFetch;
}

async function getOrCreateInstallId(): Promise<string> {
  const { value } = await Preferences.get({ key: INSTALL_ID_KEY });
  if (value) return value;

  const installId = crypto.randomUUID();
  // Preferences, deliberately NOT the Keychain: a device identifier that survives
  // app uninstall is a supercookie / App Review risk.
  await Preferences.set({ key: INSTALL_ID_KEY, value: installId });
  return installId;
}

function derivePlatform(): 'ios' | 'android' {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') return platform;
  throw new Error(`derivePlatform: unsupported platform "${platform}"`);
}

async function submitDeviceToken(token: string): Promise<void> {
  const [installId, apiFetch] = await Promise.all([getOrCreateInstallId(), getApiFetch()]);
  const platform = derivePlatform();

  await apiFetch('/social/device-tokens/', {
    method: 'POST',
    body: { token, platform, installId },
  });
  await Preferences.set({ key: DEVICE_TOKEN_KEY, value: token });
}

/**
 * Registers the app for push notifications. Attaches the `tokenReceived`
 * listener — which fires on token refresh/rotation, not the initial token —
 * before requesting one. `getToken()` is promise-based and resolves the
 * initial token directly, so unlike the old plugin's fire-and-forget
 * `register()` + `registration` listener, there's no attach-order race to
 * guard for the first token; `tokenReceived` only needs to be in place
 * before a later rotation could fire.
 *
 * Safe to call repeatedly (from onboarding and from every app bootstrap):
 * listeners are attached once per session, and permission is only requested
 * when not yet decided.
 */
export async function registerForPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  if (!listenersAttached) {
    listenersAttached = true;

    await FirebaseMessaging.addListener('tokenReceived', (event: TokenReceivedEvent) => {
      submitDeviceToken(event.token).catch((error) => {
        console.error('[pushRegistration] Failed to submit device token:', error);
      });
    });
  }

  let permStatus: PermissionStatus = await FirebaseMessaging.checkPermissions();
  if (permStatus.receive === 'prompt') {
    permStatus = await FirebaseMessaging.requestPermissions();
  }
  if (permStatus.receive !== 'granted') return;

  try {
    const { token } = await FirebaseMessaging.getToken();
    await submitDeviceToken(token);
  } catch (error) {
    // Never log the token itself.
    console.error('[pushRegistration] Failed to acquire push token:', error);
  }
}

/**
 * Tells the server to forget the current device token. Fire-and-forget by
 * design at call sites — never retried, never allowed to throw into a logout
 * flow (callers should catch).
 */
export async function deregisterDeviceToken(): Promise<void> {
  const { value: token } = await Preferences.get({ key: DEVICE_TOKEN_KEY });
  if (!token) return;

  const apiFetch = await getApiFetch();
  await apiFetch('/social/device-tokens/', {
    method: 'DELETE',
    body: { token },
  });
  // Only clear the local key once the DELETE actually completes — a thrown/
  // network error leaves it in place so a later retry can still name the
  // server-side row to deactivate.
  await Preferences.remove({ key: DEVICE_TOKEN_KEY });
}

/**
 * Deletes the token from the native push plugin via
 * FirebaseMessaging.deleteToken() — the real native call the Phase 2 stub
 * was written to receive. No-op on non-native platforms, mirroring
 * registerForPush's native gate: registerForPush() never acquires a token
 * on web, so there's nothing there to delete either.
 */
export async function deleteToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await FirebaseMessaging.deleteToken();
}

/** Full deregistration: server DELETE + local plugin cleanup. */
export async function deregisterForPush(): Promise<void> {
  await deregisterDeviceToken();
  await deleteToken();
}
